import React, { useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Paper,
  IconButton,
  Grid,
  CircularProgress,
  InputAdornment,
  Collapse,
  Alert,
  styled,
  Chip
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { alpha } from '@mui/material/styles';

import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { GenerateAndDownloadFile } from "../../services";
import { handleBlobDownload } from "../../methods";

const S3_WRITER = "SnowflakeS3Writer";

// Enhanced styled tooltip for information
const EnhancedTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} placement="top" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.surface.raised,
    color: theme.palette.text.primary,
    maxWidth: 320,
    fontSize: theme.typography.pxToRem(13),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[3],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.surface.raised,
    '&::before': {
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

// Category item styling
const CategoryItem = styled(Box)(({ theme, selected }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
  margin: theme.spacing(0.75),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.action.hover, 0.05),
    borderColor: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  },
}));

// Styled DialogTitle with close button
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  '& .MuiTypography-root': {
    fontWeight: 600,
  }
}));

const dictateInitialValue = (db) => {
  switch (db) {
    case "IUSM-GEMMS":
      return { "Patient Demographics": true };
    case "IUSM-PSYCH":
      return { "Patient Demographics": true };
    case "IUSM-CARDIO":
    default:
      return { "Patient Demographics": true };
  }
};

const DownloadFileModal = ({ entities, fileName, sql, db, setSnackbarInfo, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sampleSize, setSampleSize] = useState("100");
  const [checkedEntities, setCheckedEntities] = useState(dictateInitialValue(db));
  const [allChecked, setAllChecked] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledgementOpen, setAcknowledgementOpen] = useState(false);
  const [limitToSpecified, setLimitToSpecified] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);

  const handleDownloadOpen = () => {
    setOpen(true);
  };

  const handleDownloadClose = () => {
    setOpen(false);
    // Reset states when modal is closed
    setDownloadComplete(false);
    setLoading(false);
    setAcknowledged(false);
    setLimitToSpecified(false);
  };

  const handleAlertOpen = () => {
    setAlertOpen(true);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
    setAcknowledged(false);
    setAcknowledgementOpen(false);
    // Clear loading too, or closing mid-run leaves Download stuck disabled
    setLoading(false);
    setDownloadComplete(false);
  };

  const downloadStorageFile = async () => {
    if (!acknowledged) {
      setSnackbarInfo({
        open: true,
        message: "Please consent to the license agreement before downloading.",
        severity: 'error'
      });
      return;
    }

    handleDownloadClose();
    handleAlertOpen();
    setLoading(true);

    try {
      // Check this before fetching, or a failed run downloads the old zip
      const generateResult = await GenerateAndDownloadFile({
        body: {
          dbType: S3_WRITER,
          fileName,
          writerSQL: sql,
          db,
          sampleSize,
          checkedEntities: entities.filter(x => checkedEntities[x.friendlyName]),
          limitToSpecified
        }
      });

      if (!generateResult?.success) {
        throw new Error("Download generation did not complete successfully.");
      }

      // Use the signed URL instead of rebuilding the key - let the server
      // decide which zip belongs to this run
      const archiveResponse = await fetch(generateResult.data.signedUrl);

      if (!archiveResponse.ok) {
        throw new Error(
          `Could not retrieve the generated file (${archiveResponse.status}).`
        );
      }

      const archiveBlob = await archiveResponse.blob();

      await handleBlobDownload(archiveBlob, fileName.replace('csv', 'zip'));
      setDownloadComplete(true);

      setSnackbarInfo({
        open: true,
        message: "File downloaded successfully!",
        severity: 'success'
      });
    } catch (error) {
      console.error(error);
      setSnackbarInfo({
        open: true,
        message: error?.message
          ? `Error downloading file: ${error.message}`
          : "Error downloading file. Please try again.",
        severity: 'error'
      });
    } finally {
      setLoading(false);
      handleAlertClose();
    }
  };

  const handleChange = (friendlyName) => (event) => {
    setCheckedEntities({
      ...checkedEntities,
      [friendlyName]: event.target.checked
    });
  };

  const handleSelectAll = () => {
    const newAllChecked = !allChecked;
    setAllChecked(newAllChecked);

    const updatedCheckedEntities = {};
    entities.forEach((entity) => {
      if (!entity.disabled) {
        updatedCheckedEntities[entity.friendlyName] = newAllChecked;
      }
    });

    setCheckedEntities(updatedCheckedEntities);
  };

  const handleSampleSizeChange = (e) => {
    const value = e.target.value;
    if (value === "ALL" || (parseInt(value) > 10000)) {
      setSnackbarInfo({
        open: true,
        message: "Please contact support to download more than 10,000 records.",
        severity: 'error'
      });
    }
    setSampleSize(value);
  };

  const toggleCategory = (friendlyName) => {
    setCheckedEntities(prev => ({
      ...prev,
      [friendlyName]: !prev[friendlyName]
    }));
  };

  // Count selected categories
  const selectedCount = Object.values(checkedEntities).filter(Boolean).length;
  const activeEntities = entities?.filter(entity => !entity.disabled) || [];

  return (
    <div>
      <Button
        variant="outlined"
        startIcon={<CloudDownloadIcon />}
        sx={{
          textTransform: 'none',
          fontSize: '0.875rem',
          px: 2,
          py: compact ? 0.5 : 1,
          fontWeight: 500,
          minHeight: compact ? 30 : 36,
          color: '#04aeea',
          borderColor: '#04aeea',
          '&:hover': {
            borderColor: '#038bbf',
            backgroundColor: alpha('#04aeea', 0.1)
          },
          '&.Mui-disabled': {
            color: alpha('#04aeea', 0.3),
            borderColor: alpha('#04aeea', 0.3),
          }
        }}
        disabled={fileName === null || loading}
        onClick={handleDownloadOpen}
      >
        Download
      </Button>

      {/* Progress Dialog */}
      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: 2 }
        }}
      >
        <StyledDialogTitle>
          <Typography variant="h6">Download Progress</Typography>
          <IconButton edge="end" onClick={handleAlertClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {downloadComplete ? (
            <>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="body1" align="center">
                Download completed successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                The file has been saved to your downloads folder.
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" align="center">
                Preparing your download...
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                The file will appear in your downloads folder when ready.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleAlertClose}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: '#04aeea',
              '&:hover': {
                backgroundColor: '#038bbf',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledgment Dialog */}
      <Dialog
        open={acknowledgementOpen}
        onClose={() => setAcknowledgementOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: 2 }
        }}
      >
        <StyledDialogTitle>
          <Typography variant="h6">User Acknowledgment</Typography>
          <IconButton edge="end" onClick={() => setAcknowledgementOpen(false)} aria-label="close">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent dividers sx={{ pb: 2 }}>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha('#f5f5f5', 0.5), borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              USER ACKNOWLEDGMENT
            </Typography>
            <Typography variant="body2" paragraph>
              Pursuant to the IUSM-E RWE Data Licensing Agreement and the IU Data Management Policy DM-01 Data Sharing and Handling (DHS), as the recipient of this restricted dataset, you agree to:
            </Typography>
            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
              <Typography component="li" variant="body2">
                Never use the information for personal gain or profit, the gain or profit of others, to satisfy curiosity, or to engage in academic, personal, or research misconduct.
              </Typography>
              <Typography component="li" variant="body2">
                Immediately report any misuse of information to the appropriate authorities.
              </Typography>
              <Typography component="li" variant="body2">
                Always log off or lock your workstation when you step away, even for a moment.
              </Typography>
              <Typography component="li" variant="body2">
                Be held accountable for the specific use and sharing of this data.
              </Typography>
              <Typography component="li" variant="body2">
                Be held accountable for the specific use and sharing of this data.Never expose the data to any unauthorized AI platforms unless approved in writing by IUSM-Evansville.
              </Typography>
            </Box>
            <Box sx={{ mt: 2, p: 1.5, borderLeft: '4px solid #04aeea', backgroundColor: alpha('#04aeea', 0.05) }}>
              <Typography variant="body2" fontWeight={500}>
                {`NOTE: AAny presented or published research material utilizing this data must include the following acknowledgement "CRC/Sidus Insights database used for RWE research in collaboration with Mary O'Daniel Stone and Bill Stone Center for Child and Adolescent Psychiatry at Indiana University School of Medicine – Evansville, Indiana"`}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={() => { setAcknowledged(true); setAcknowledgementOpen(false); }}
            variant="contained"
            sx={{
              minWidth: 120,
              backgroundColor: '#04aeea',
              '&:hover': {
                backgroundColor: '#038bbf',
              }
            }}
          >
            I Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Download Modal */}
      <Dialog
        open={open}
        onClose={handleDownloadClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 }
        }}
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CloudDownloadIcon sx={{ mr: 1.5 }} />
            <Typography variant="h6">Download File</Typography>
          </Box>
          <IconButton edge="end" onClick={handleDownloadClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Sample Size Section */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              backgroundColor: alpha('#f8f9fa', 0.7)
            }}
          >
            <Typography variant="subtitle2" fontWeight={500} gutterBottom>
              Sample Size
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mt: 0.75 }}>
                {`Enter the number of records to download or "ALL" for no sampling`}
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                value={sampleSize}
                onChange={handleSampleSizeChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <EnhancedTooltip
                        title={
                          <Typography variant="body2">
                            Please contact support to download more than 10,000 records.
                          </Typography>
                        }
                      >
                        <HelpOutlineIcon fontSize="small" color="action" />
                      </EnhancedTooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />
            </Box>
          </Box>

          {/* Data Categories Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight={500}>
                Data Categories
              </Typography>
              <Chip
                label={`${selectedCount} selected`}
                size="small"
                sx={{ ml: 1, height: 24, backgroundColor: alpha('#04aeea', 0.1) }}
              />
            </Box>

            <Button
              variant="text"
              size="small"
              onClick={handleSelectAll}
              sx={{ ml: 1 }}
            >
              {allChecked ? "Deselect All" : "Select All"}
            </Button>
          </Box>

          {/* Categories Grid */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              mb: 3
            }}
          >
            {activeEntities.map((entity) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entity.friendlyName}>
                <CategoryItem
                  selected={checkedEntities[entity.friendlyName] || false}
                  onClick={() => toggleCategory(entity.friendlyName)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={checkedEntities[entity.friendlyName] || false}
                      color="primary"
                      size="small"
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={checkedEntities[entity.friendlyName] ? 500 : 400}
                    >
                      {entity.friendlyName}
                    </Typography>
                  </Box>

                  {entity.enableTooltip && (
                    <EnhancedTooltip
                      title={
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Definition
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {entity.tooltip?.definition}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Example
                          </Typography>
                          <Typography variant="body2">
                            {entity.tooltip?.example}
                          </Typography>
                        </Box>
                      }
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        color="action"
                        sx={{ opacity: 0.7 }}
                      />
                    </EnhancedTooltip>
                  )}
                </CategoryItem>
              </Grid>
            ))}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={limitToSpecified}
                    onChange={() => setLimitToSpecified(!limitToSpecified)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">
                      Constrain downloaded data to filtered values
                    </Typography>
                    <EnhancedTooltip
                      title={
                        <Typography variant="body2">
                          Check this box to limit the downloaded records in filtered tables to only selected records.
                          For instance, if you have chosen to filter your query on medication history, and also
                          chosen to download medication history records, only medication history records
                          pertaining to the filtered values will be downloaded.
                        </Typography>
                      }
                    >
                      <HelpOutlineIcon
                        fontSize="small"
                        color="action"
                        sx={{ ml: 1, opacity: 0.7 }}
                      />
                    </EnhancedTooltip>
                  </Box>
                }
              />
            </Box>
          </Box>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={() => acknowledged ? setAcknowledged(false) : setAcknowledgementOpen(true)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight={500} color={acknowledged ? 'primary' : 'inherit'}>
                I consent to the license agreement
              </Typography>
            }
          />

          {/* Alert for license information */}
          <Collapse in={acknowledged}>
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                mt: 2,
                backgroundColor: alpha('#e3f2fd', 0.5)
              }}
            >
              <Typography variant="body2">
                {`You've agreed to follow IU Data Management Policy DM-01 requirements and
                    acknowledge that any published research using this data must include proper citation.`}
              </Typography>
            </Alert>
          </Collapse>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button
            onClick={handleDownloadClose}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: 'text.primary'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={downloadStorageFile}
            variant="contained"
            disabled={sampleSize == null || sampleSize === '' || !acknowledged}
            startIcon={<CloudDownloadIcon />}
            sx={{
              backgroundColor: '#04aeea',
              '&:hover': {
                backgroundColor: '#038bbf',
              },
              '&.Mui-disabled': {
                backgroundColor: alpha('#04aeea', 0.3),
              }
            }}
          >
            Download File
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DownloadFileModal;